import {Link} from 'react-router-dom';
import {formatDate} from '@common/lib/utils';
import {Badge} from '@common-web/components/ui/badge';
import {buildGroupMemberSearchPath} from '@common-web/hooks/useMemberSearchUrlState';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {
  MEMBER_PILL_SIZE_CLASS,
  MemberStatusBadge,
  type MemberStatusBadgeStatus,
} from '@client/pages/client/member-profile/MemberStatusBadge';

function KeyFact({label, value}: {label: string; value: React.ReactNode}) {
  return (
    <div className='flex min-w-[90px] flex-col gap-0.5'>
      <span className='text-xs font-semibold uppercase tracking-wide text-brand-gray'>{label}</span>
      <span className='text-[13px] font-medium text-brand-navy'>{value}</span>
    </div>
  );
}

export type MemberContextStripProps = {
  /** Omit to hide the fact for sources with no coverage tier. */
  coverageTier?: string | null;
  effectiveDate: string | null;
  /** Omit to hide the fact for sources with no employee id. */
  employeeId?: string | null;
  groupId: string | null;
  groupMemberCount?: number | null;
  groupName: string | null;
  /** Label for the inactive status pill. */
  inactiveLabel?: string;
  isActive: boolean;
  /** When set, shows "Change" next to coverage tier. */
  onChangeTier?: () => void;
  /** At-a-glance member tags (e.g. VBA / App User / Dependent). */
  tags?: string[];
  tenantName: string | null;
  /** Omit to hide the fact (e.g. an active VIP). */
  termDate?: string | null;
};

export function MemberContextStrip({
  coverageTier,
  effectiveDate,
  employeeId,
  groupId,
  groupMemberCount,
  groupName,
  inactiveLabel = 'Termed',
  isActive,
  onChangeTier,
  tags,
  tenantName,
  termDate,
}: MemberContextStripProps) {
  const isInternal = useIsInternal();
  const groupLabel = groupName || groupId || '—';
  const status: MemberStatusBadgeStatus = isActive ? 'active' : 'termed';
  const otherMembersPath = groupId
    ? buildGroupMemberSearchPath(groupId, groupName ?? undefined)
    : null;

  return (
    <div
      className={
        // Internal: demo's soft-orange coverage band (accentSoft wash, no border)
        // rather than a plain white card.
        isInternal
          ? 'flex flex-wrap items-start gap-x-8 gap-y-3 rounded-xl bg-[#fdeedd] px-5 py-4'
          : 'flex flex-wrap items-start gap-x-8 gap-y-3 rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm'
      }
    >
      <KeyFact
        label='Group'
        value={
          <span className='inline-flex items-center gap-1.5'>
            {groupId ? (
              <Link
                to={`/groups/${encodeURIComponent(groupId)}`}
                className='font-semibold text-brand-blue hover:underline'
              >
                {groupLabel}
              </Link>
            ) : (
              <span className='font-semibold'>{groupLabel}</span>
            )}
            {tenantName && (
              <Badge variant='outline' className={MEMBER_PILL_SIZE_CLASS}>
                {tenantName}
              </Badge>
            )}
          </span>
        }
      />
      <KeyFact
        label='Status'
        value={
          <MemberStatusBadge
            status={status}
            label={isActive ? 'Active' : inactiveLabel}
            tone='context'
            size='context'
          />
        }
      />
      {coverageTier !== undefined && (
        <KeyFact
          label='Coverage Tier'
          value={
            <span className='inline-flex items-center gap-1.5'>
              {coverageTier?.trim() ? coverageTier : '—'}
              {onChangeTier && isActive && (
                <button
                  type='button'
                  onClick={onChangeTier}
                  className='text-xs font-semibold text-brand-blue hover:underline'
                >
                  Change
                </button>
              )}
            </span>
          }
        />
      )}
      <KeyFact label='Effective' value={formatDate(effectiveDate, 'MMM D, YYYY', '—')} />
      {termDate !== undefined && (
        <KeyFact label='Term Date' value={formatDate(termDate, 'MMM D, YYYY', '—')} />
      )}
      {employeeId !== undefined && (
        <KeyFact
          label='Employee ID'
          value={<span className='font-mono'>{employeeId?.trim() ? employeeId : '—'}</span>}
        />
      )}
      {tags && tags.length > 0 && (
        <KeyFact
          label='Tags'
          value={
            <span className='inline-flex flex-wrap items-center gap-1.5'>
              {tags.map((tag) => (
                <Badge
                  key={tag}
                  className={`border-0 ${MEMBER_PILL_SIZE_CLASS} ${
                    isInternal ? 'bg-primary/10 text-primary' : 'bg-brand-blue/10 text-brand-blue'
                  }`}
                >
                  {tag}
                </Badge>
              ))}
            </span>
          }
        />
      )}
      {otherMembersPath && (
        <Link
          to={otherMembersPath}
          className='ml-auto self-center whitespace-nowrap text-[12px] text-brand-blue hover:underline'
        >
          View other members in this group
          {typeof groupMemberCount === 'number' ? ` (${groupMemberCount})` : ''}
        </Link>
      )}
    </div>
  );
}
